import { toast } from "react-toastify";
import { apiClient } from "../../../api/httpClient";
import { extractErrorMessage } from "../../../globals/utils/error-handler";
import logger from "../../../globals/utils/logger";
import { ROLE_HOME_MAP } from "./bondFormConfig";

/**
 * @typedef {import("./bondFormConfig").BondFormKindConfig} BondFormKindConfig
 */

/**
 * @typedef {Object} BondFormBaseContext
 * @property {BondFormKindConfig} config
 * @property {object} globalState
 * @property {React.Dispatch<React.SetStateAction<object>>} setGlobalState
 * @property {boolean} isGuestRequest
 * @property {string} cacheId
 * @property {string | undefined} cache_id
 * @property {React.Dispatch<React.SetStateAction<boolean>>} setIsLoading
 * @property {React.Dispatch<React.SetStateAction<number>>} setActiveStep
 * @property {React.Dispatch<React.SetStateAction<object>>} setErrors
 * @property {ReturnType<import("react-router-dom").useNavigate>} navigate
 * @property {string} role
 * @property {() => object} buildInitialState
 * @property {React.Dispatch<React.SetStateAction<string>>} setSubmissionError
 * @property {React.Dispatch<React.SetStateAction<string>>} setFailedBondType
 * @property {React.Dispatch<React.SetStateAction<boolean>>} setSupportModalOpen
 * @property {React.Dispatch<React.SetStateAction<boolean>>} setIsDuplicateDialogOpen
 * @property {React.Dispatch<React.SetStateAction<number | null>>} setDuplicateBondId
 * @property {React.Dispatch<React.SetStateAction<object | null>>} setPendingPayload
 */

/**
 * @param {BondFormBaseContext} ctx
 * @param {object} payload
 * @returns {Promise<boolean>} true when duplicate dialog was shown
 */
export async function runDuplicateBondCheck(ctx, payload) {
  const { config, setDuplicateBondId, setPendingPayload, setIsDuplicateDialogOpen, setIsLoading } =
    ctx;
  const { duplicateCheck } = config;
  if (!duplicateCheck?.enabled) return false;

  const { endpoint, nestedKey, principalsKey } = duplicateCheck;
  try {
    const dupResponse = await apiClient(endpoint, "POST", {
      bond_data: payload,
      [nestedKey]: payload[nestedKey],
      [principalsKey]: payload[principalsKey],
    });
    if (dupResponse?.duplicate) {
      setDuplicateBondId(dupResponse?.bond_id || null);
      setPendingPayload(payload);
      setIsDuplicateDialogOpen(true);
      setIsLoading(false);
      return true;
    }
  } catch (err) {
    logger.debug("useBondFormBase", "duplicate check failed, submitting bond", {
      err,
      endpoint,
    });
  }
  return false;
}

/**
 * @param {BondFormBaseContext} ctx
 * @param {object} response
 * @param {string} logContext
 */
export function runBondCreateSuccessUiSteps(ctx, response, logContext) {
  const {
    config,
    setActiveStep,
    setGlobalState,
    setErrors,
    navigate,
    role,
    buildInitialState,
  } = ctx;

  try {
    const bondId =
      response?.bond_id ||
      response?.id ||
      response?.bond?.bond_id ||
      response?.data?.bond_id;
    if (bondId) {
      sessionStorage.setItem(
        "flashToast",
        JSON.stringify({
          message: `Your Recent Bond Request #${bondId} was Submitted!`,
          link: `${config.updateRoutePrefix}${bondId}`,
        })
      );
    }
  } catch (err) {
    logger.debug(logContext, "flashToast sessionStorage failed", { err });
  }

  setActiveStep(0);
  setGlobalState(buildInitialState());
  setErrors({});
  navigate(ROLE_HOME_MAP[role] || "/");
}

/**
 * @param {BondFormBaseContext} ctx
 * @param {object} response
 * @param {string} logContext
 */
export async function runPostCreateBondCleanup(ctx, response, logContext) {
  const { cacheId, cache_id } = ctx;

  if (cacheId || cache_id) {
    await apiClient(`/bond-cache/${cacheId || cache_id}/`, "DELETE");
  }

  runBondCreateSuccessUiSteps(ctx, response, logContext);
}

/**
 * @param {BondFormBaseContext} ctx
 * @param {object} payload
 * @param {object} [options]
 */
export async function finalizeBondCreation(ctx, payload, options = {}) {
  const { finalizeAfterCreate = true } = options;
  const {
    config,
    setIsLoading,
    setSubmissionError,
    setFailedBondType,
    setSupportModalOpen,
  } = ctx;

  let response;
  try {
    response = await apiClient(config.createEndpoint, "POST", payload);
  } catch (error) {
    const message = extractErrorMessage(error);
    setSubmissionError(message);
    setFailedBondType(config.failedBondType);
    setSupportModalOpen(true);
    if (finalizeAfterCreate) setIsLoading(false);
    return;
  }

  if (!finalizeAfterCreate) {
    return response;
  }

  setIsLoading(false);
  localStorage.removeItem("guestReferralData");
  toast.success(
    config.payloadKind === "guardianship"
      ? "Bond created successfully"
      : "Bond created successfully!"
  );

  try {
    await runPostCreateBondCleanup(ctx, response, "useBondFormBase");
  } catch (error) {
    logger.warn("useBondFormBase", "post-create cleanup failed after successful bond create", {
      err: error,
    });
  }

  return response;
}
